import { openUrl } from '@tauri-apps/plugin-opener';
import type { Component } from 'solid-js';
import { For, Show, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  onBack: () => void;
};

const FEEDBACK_CATEGORIES = [
  { value: 'bug', label: 'Something is not working' },
  { value: 'confusing', label: 'Something is confusing' },
  { value: 'idea', label: 'I have an idea' },
  { value: 'like', label: 'Something I like' },
  { value: 'other', label: 'Something else' },
] as const;

export function buildFeedbackMailto(category: string, message: string): string {
  const subject = `ChorePop feedback: ${category}`;
  const body = `Category: ${category}\n\nFeedback:\n${message}\n\nSent from ChorePop guardian feedback`;
  return `mailto:support@chorepop.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export const FeedbackScreen: Component<Props> = (props) => {
  const [category, setCategory] =
    createSignal<(typeof FEEDBACK_CATEGORIES)[number]['value']>('bug');
  const [message, setMessage] = createSignal('');
  const [opening, setOpening] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [emailOpened, setEmailOpened] = createSignal(false);

  const openEmail = async () => {
    const trimmedMessage = message().trim();
    if (trimmedMessage.length < 3) {
      setEmailOpened(false);
      setError('Tell us what happened or what you would like ChorePop to improve.');
      return;
    }

    const categoryLabel =
      FEEDBACK_CATEGORIES.find((option) => option.value === category())?.label ?? 'Something else';
    setOpening(true);
    setError(null);
    setEmailOpened(false);
    try {
      await openUrl(buildFeedbackMailto(categoryLabel, trimmedMessage));
      setEmailOpened(true);
    } catch {
      setError(
        'ChorePop could not open your email app. You can send your feedback directly to support@chorepop.com.',
      );
    } finally {
      setOpening(false);
    }
  };

  return (
    <ActionScreen
      eyebrow="Help improve ChorePop"
      title="Report a problem or idea"
      onBack={props.onBack}
      footer={
        <button
          type="button"
          class="primary action-screen-primary"
          disabled={opening()}
          onClick={() => void openEmail()}
        >
          {opening() ? 'Opening…' : 'Open email app'}
        </button>
      }
    >
      <p class="action-screen-intro">
        Tell us about a bug, a confusing screen, something you like, or an idea for ChorePop.
      </p>
      <section class="form-card">
        <label class="field" for="feedback-category">
          <span class="field-label">What would you like to tell us?</span>
          <select
            id="feedback-category"
            value={category()}
            disabled={opening()}
            onChange={(event) =>
              setCategory(
                event.currentTarget.value as (typeof FEEDBACK_CATEGORIES)[number]['value'],
              )
            }
          >
            <For each={FEEDBACK_CATEGORIES}>
              {(option) => <option value={option.value}>{option.label}</option>}
            </For>
          </select>
        </label>
        <div class="field">
          <label class="field-label" for="feedback-message">
            Your feedback
          </label>
          <textarea
            id="feedback-message"
            aria-describedby="feedback-message-hint"
            class="field-textarea"
            rows={7}
            maxlength={1500}
            value={message()}
            disabled={opening()}
            placeholder="Tell us what happened, what you expected, or what would make ChorePop better."
            onInput={(event) => setMessage(event.currentTarget.value)}
          />
          <span id="feedback-message-hint" class="field-hint">
            Do not include passwords, PINs, or payment details.
          </span>
        </div>
      </section>
      <p class="field-hint">
        This opens your email app with the message ready to review. Nothing is sent until you press
        Send in your email app.
      </p>
      <Show when={emailOpened()}>
        <p class="form-success" role="status">
          Your email app is ready. Review the message, then press Send there.
        </p>
      </Show>
      <Show when={error()}>
        {(message) => (
          <p class="form-error" role="alert">
            {message()}
          </p>
        )}
      </Show>
    </ActionScreen>
  );
};
